import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOTION, NEUTRAL, SURFACE } from "@/theme/tokens";

// Strip everything that isn't a digit.
const NON_DIGIT = /\D/g;

// iOS Safari zooms the page when it focuses an input whose font is smaller than this.
const IOS_NO_ZOOM_FONT_SIZE = "16px";

// The real input covers the whole row so every tap lands on it, but draws nothing itself.
const OVERLAY_INPUT_SX: SxProps<Theme> = {
	position: "absolute",
	// Stay above the cells even if they later gain position, transform or opacity, so taps still reach the input.
	zIndex: 1,
	inset: 0,
	width: "100%",
	height: "100%",
	padding: 0,
	margin: 0,
	border: 0,
	outline: "none",
	background: "transparent",
	color: "transparent",
	caretColor: "transparent",
	fontSize: IOS_NO_ZOOM_FONT_SIZE,
	WebkitTapHighlightColor: "transparent",
	"&::selection": { background: "transparent" },
	// Browsers paint their own autofill background, which `background` can't override; hide it so the cells show.
	"&:-webkit-autofill, &:autofill": { opacity: 0 },
};

export interface SystemCodeInputProps {
	/** Number of code cells. Defaults to 6. */
	length?: number;
	/** The digits shown — up to `length`. The input keeps what was typed, so the parent must accept every `onChange`. */
	value: string;
	/** Fires with the sanitized (digits-only, capped) value. */
	onChange: (value: string) => void;
	/** Fires once all cells are filled (e.g. to auto-submit). */
	onComplete?: (value: string) => void;
	disabled?: boolean;
	/** Focus the input on mount. */
	autoFocus?: boolean;
	/** Render cells in the error color. */
	error?: boolean;
	/** Name of the input, so the code is in FormData. */
	name?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
}

/**
 * Segmented N-cell code entry. One real input sits over display-only cells because iOS AutoFill
 * inserts the whole code at once into the focused field. The forwarded ref points at that input.
 */
export const SystemCodeInput = forwardRef<HTMLInputElement, SystemCodeInputProps>(function SystemCodeInput(
	{ length = 6, value, onChange, onComplete, disabled = false, autoFocus = false, error = false, name, dataTestId, sx },
	forwardedRef,
) {
	const theme = useTheme();
	const { t } = useTranslation();

	const inputRef = useRef<HTMLInputElement | null>(null);
	const setInputRef = useForkRef(inputRef, forwardedRef);
	const [focused, setFocused] = useState(false);

	// Sanitize + cap, then notify. Returns the sanitized value for callers that need it.
	const commit = useCallback(
		(next: string) => {
			const sanitized = next.replace(NON_DIGIT, "").slice(0, length);
			if (sanitized !== value) {
				onChange(sanitized);
			}
			if (sanitized.length === length) {
				onComplete?.(sanitized);
			}
			return sanitized;
		},
		[length, value, onChange, onComplete],
	);

	// Keep the input's DOM value matching `value` so FormData is always correct.
	useEffect(() => {
		if (inputRef.current && inputRef.current.value !== value) {
			inputRef.current.value = value;
		}
	}, [value]);

	// A native listener, because WebOTP sets `.value` and dispatches `input`, which React's onChange can miss.
	useEffect(() => {
		const input = inputRef.current;
		if (!input) {
			return;
		}
		const onNativeInput = () => {
			const sanitized = commit(input.value);
			if (input.value !== sanitized) {
				input.value = sanitized;
			}
		};
		// A whole code replaces the field, so inserting it into a partly typed field doesn't append to it.
		const onBeforeInput = (event: InputEvent) => {
			// iOS AutoFill may send insertReplacementText, which carries the text in dataTransfer instead of data.
			const insertedText = event.data !== null ? event.data : event.dataTransfer?.getData("text/plain");
			const insertedDigits = insertedText?.replace(NON_DIGIT, "");
			if (insertedDigits === undefined || insertedDigits.length < length) {
				return;
			}
			event.preventDefault();
			input.value = commit(insertedDigits);
		};
		// Android keyboards (Gboard's clipboard and code suggestions) can set the value with no input event at all.
		const onSelectionChange = () => {
			if (document.activeElement === input && input.value !== value) {
				onNativeInput();
			}
		};
		input.addEventListener("input", onNativeInput);
		input.addEventListener("beforeinput", onBeforeInput);
		document.addEventListener("selectionchange", onSelectionChange);
		return () => {
			input.removeEventListener("input", onNativeInput);
			input.removeEventListener("beforeinput", onBeforeInput);
			document.removeEventListener("selectionchange", onSelectionChange);
		};
	}, [commit, length, value]);

	useEffect(() => {
		if (autoFocus && !disabled) {
			inputRef.current?.focus();
		}
	}, [autoFocus, disabled]);

	const cells = useMemo(() => Array.from({ length }, (_, index) => index), [length]);

	// A paste replaces the field and keeps only its digits, so "Code: 394-173" fills in as 394173.
	const handlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLInputElement>) => {
			event.preventDefault();
			commit(event.clipboardData.getData("text"));
		},
		[commit],
	);

	// Keep the caret at the end so typing appends and Backspace removes the last digit.
	const pinCaretToEnd = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
		const input = event.currentTarget;
		const end = input.value.length;
		if (input.selectionStart !== end || input.selectionEnd !== end) {
			input.setSelectionRange(end, end);
		}
	}, []);

	const activeIndex = focused && !disabled ? Math.min(value.length, length - 1) : null;

	return (
		<Box
			data-testid={dataTestId}
			// Session replay masks inputs but not text, so hide the digits drawn in the cells.
			className="mp-mask"
			// A numeric code always reads left-to-right, even in RTL locales.
			dir="ltr"
			sx={{
				display: "flex",
				justifyContent: "center",
				gap: { xs: "0.4rem", sm: "0.6rem" },
				width: "100%",
				...sx,
				// After the caller's sx so the overlay input stays anchored to this row.
				position: "relative",
			}}
		>
			<Box
				component="input"
				ref={setInputRef}
				type="text"
				name={name}
				defaultValue={value}
				autoComplete="one-time-code"
				inputMode="numeric"
				// No maxLength: Safari cuts an inserted code to the free space before our handlers see it; commit caps the length.
				disabled={disabled}
				aria-label={t("verification.code")}
				aria-invalid={error}
				data-testid={dataTestId ? `${dataTestId}-input` : undefined}
				onPaste={handlePaste}
				onSelect={pinCaretToEnd}
				onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					setFocused(true);
					pinCaretToEnd(event);
				}}
				onBlur={() => setFocused(false)}
				sx={OVERLAY_INPUT_SX}
			/>
			{cells.map((index) => {
				const isActive = index === activeIndex;
				const borderColor = error ? theme.palette.error.main : isActive ? theme.palette.primary.main : index < value.length ? NEUTRAL[400] : NEUTRAL[300];
				return (
					<Box
						key={index}
						aria-hidden="true"
						data-testid={dataTestId ? `${dataTestId}-cell-${index}` : undefined}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: { xs: "2.5rem", sm: "3rem" },
							height: { xs: "3rem", sm: "3.5rem" },
							minWidth: 0,
							flex: "0 1 auto",
							fontSize: { xs: "1.25rem", sm: "1.5rem" },
							fontWeight: 600,
							color: theme.palette.text.primary,
							backgroundColor: disabled ? NEUTRAL[100] : SURFACE,
							border: "1px solid",
							borderColor,
							borderRadius: `${theme.shape.borderRadius}px`,
							boxShadow: isActive ? `0 0 0 2px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.2)}` : "none",
							transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						}}
					>
						{value[index]}
					</Box>
				);
			})}
		</Box>
	);
});
